import {
  MAX_ANNUAL_HOURS, DEFAULT_ANNUAL_HOURS,
  type BenchmarkStatus, type PayBand, type PayBasis, type SetPayBandInput,
} from './compensationTypes';

/**
 * Pure helpers for employer pay bands: display formatting, hourly/annual conversion, the "is this rate inside the band" comparison
 * used by Add Employee, and the pay band form validation. Nothing here reads or writes an employee's pay, and nothing here decides
 * what anyone should be paid: the band is the employer's own guide, and the comparison only ever produces a non-blocking note.
 */

// ---- formatting ----

const wholeDollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const dollarsAndCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "$24.00" for an hourly amount; "$55,000" (or "$55,000.50") for an annual one. */
export function formatMoney(value: number, basis: PayBasis): string {
  if (basis === 'Annual' && Number.isInteger(value)) return wholeDollars.format(value);
  return dollarsAndCents.format(value);
}

export const basisUnit = (basis: PayBasis): string => (basis === 'Hourly' ? '/ hr' : '/ yr');

/** "$55,000 – $75,000 / yr" or "$24.00 – $32.00 / hr". */
export function formatBandRange(band: { basis: PayBasis; minimum: number; maximum: number }): string {
  return `${formatMoney(band.minimum, band.basis)} – ${formatMoney(band.maximum, band.basis)} ${basisUnit(band.basis)}`;
}

export function formatHours(hours: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(hours);
}

/** The Roles list's Benchmark Status column. An empty string for NotPermitted: the cell shows an em dash instead. */
export const benchmarkStatusLabel = (status: BenchmarkStatus): string => (
  status === 'Available' ? 'Available' : status === 'NoSocCode' ? 'No SOC code' : status === 'NoData' ? 'No data imported' : ''
);

// ---- conversion and comparison ----

/** Half away from zero at 2 decimals (the server's rule for converted benchmark values). */
export function roundMoney(value: number): number {
  return Math.sign(value) * (Math.round(Math.abs(value) * 100 + 1e-7) / 100);
}

/** `value * hours` (Hourly to Annual) or `value / hours` (Annual to Hourly) with the disclosed hours; no other adjustment. Not rounded. */
export function convertAmount(value: number, from: PayBasis, to: PayBasis, annualHours: number): number {
  if (from === to) return value;
  return from === 'Hourly' ? value * annualHours : value / annualHours;
}

export type BandPosition = 'below' | 'within' | 'above';

export interface BandComparison {
  position: BandPosition;
  /** The rate in the band's own basis (equal to the typed rate when no conversion was needed), rounded to 2 decimals for display. */
  comparedValue: number;
  comparedBasis: PayBasis;
  /** True when hourly and annual had to be converted with the band's disclosed hours. */
  converted: boolean;
}

const EPSILON = 1e-7;

/**
 * Where `rate` sits against a band, after converting between hourly and annual with the BAND's own disclosed annual hours.
 * Both ends are inclusive (a rate equal to the minimum or the maximum is inside the band). Returns null when the comparison cannot
 * be made honestly: the rate's basis is unknown (a pay type that is neither hourly nor annual), or a value is not a positive number.
 */
export function compareRateToBand(
  rate: number,
  rateBasis: PayBasis | null,
  band: { basis: PayBasis; minimum: number; maximum: number; annualHoursAssumption: number },
): BandComparison | null {
  if (rateBasis === null || !Number.isFinite(rate) || rate <= 0) return null;
  if (!Number.isFinite(band.annualHoursAssumption) || band.annualHoursAssumption <= 0) return null;
  const compared = convertAmount(rate, rateBasis, band.basis, band.annualHoursAssumption);
  const position: BandPosition = compared < band.minimum - EPSILON ? 'below' : compared > band.maximum + EPSILON ? 'above' : 'within';
  return { position, comparedValue: roundMoney(compared), comparedBasis: band.basis, converted: rateBasis !== band.basis };
}

/** Add Employee's pay type (Compensation enum: 1 Hourly, 2 Salary) as a basis; any other pay type has no honest comparison. */
export function basisForPayType(payType: number | null | undefined): PayBasis | null {
  if (payType === 1) return 'Hourly';
  if (payType === 2) return 'Annual';
  return null;
}

// ---- the pay band form ----

export interface PayBandForm {
  basis: PayBasis;
  minimum: string;
  midpoint: string;
  maximum: string;
  annualHours: string;
  note: string;
  reviewed: boolean;
}

export type PayBandFormKey = keyof PayBandForm;

export interface PayBandIssue {
  key: PayBandFormKey;
  message: string;
}

export const NOTE_MAX_LENGTH = 500;

export const emptyPayBandForm = (): PayBandForm => ({
  basis: 'Annual', minimum: '', midpoint: '', maximum: '', annualHours: String(DEFAULT_ANNUAL_HOURS), note: '', reviewed: false,
});

/** The edit form starts from the current band, but the review acknowledgement is always asked again for a new version of the band. */
export function formFromBand(band: Pick<PayBand, 'basis' | 'minimum' | 'midpoint' | 'maximum' | 'annualHoursAssumption' | 'note'>): PayBandForm {
  return {
    basis: band.basis, minimum: String(band.minimum), midpoint: String(band.midpoint), maximum: String(band.maximum),
    annualHours: String(band.annualHoursAssumption), note: band.note ?? '', reviewed: false,
  };
}

/** Whole or 2-decimal amount, `$` and thousands separators tolerated; null when it is not that shape or not above zero. */
export function parseMoneyInput(raw: string): number | null {
  const text = raw.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) && value > 0 ? value : null;
}

const CAPS: Record<PayBasis, number> = { Hourly: 10_000, Annual: 100_000_000 };

/** Every problem with the form, in the order the fields appear. Messages never echo what was typed. */
export function validatePayBandForm(form: PayBandForm): PayBandIssue[] {
  const issues: PayBandIssue[] = [];
  const amounts: Record<'minimum' | 'midpoint' | 'maximum', number | null> = { minimum: null, midpoint: null, maximum: null };
  for (const key of ['minimum', 'midpoint', 'maximum'] as const) {
    const value = parseMoneyInput(form[key]);
    if (value === null) issues.push({ key, message: `Enter the ${key} as an amount above zero, with up to 2 decimals.` });
    else if (value >= CAPS[form.basis]) issues.push({ key, message: `The ${key} is higher than this tool accepts for a${form.basis === 'Hourly' ? 'n hourly' : 'n annual'} band. Check the amount and the pay basis.` });
    else amounts[key] = value;
  }
  const { minimum, midpoint, maximum } = amounts;
  if (minimum !== null && midpoint !== null && minimum > midpoint) issues.push({ key: 'minimum', message: 'The minimum cannot be higher than the midpoint.' });
  if (midpoint !== null && maximum !== null && midpoint > maximum) issues.push({ key: 'midpoint', message: 'The midpoint cannot be higher than the maximum.' });
  if (minimum !== null && maximum !== null && midpoint === null && minimum > maximum) issues.push({ key: 'minimum', message: 'The minimum cannot be higher than the maximum.' });

  const hoursText = form.annualHours.trim();
  const hours = /^\d+(\.\d{1,2})?$/.test(hoursText) ? Number(hoursText) : null;
  if (hours === null || hours < 1 || hours > MAX_ANNUAL_HOURS) {
    issues.push({ key: 'annualHours', message: `Enter the annual hours as a number from 1 to ${MAX_ANNUAL_HOURS.toLocaleString('en-US')}.` });
  }
  if (form.note.length > NOTE_MAX_LENGTH) issues.push({ key: 'note', message: `The note can be at most ${NOTE_MAX_LENGTH} characters.` });
  if (!form.reviewed) issues.push({ key: 'reviewed', message: 'Confirm that you have reviewed this pay band before saving it.' });
  return issues;
}

/** The request body for a form that passed `validatePayBandForm`. `expectedConcurrencyVersion` is the version of the current band (when there is one). */
export function inputFromPayBandForm(form: PayBandForm, expectedConcurrencyVersion?: number): SetPayBandInput {
  const note = form.note.trim();
  return {
    basis: form.basis,
    minimum: parseMoneyInput(form.minimum)!,
    midpoint: parseMoneyInput(form.midpoint)!,
    maximum: parseMoneyInput(form.maximum)!,
    annualHoursAssumption: Number(form.annualHours.trim()),
    reviewAcknowledged: form.reviewed,
    ...(note ? { note } : {}),
    ...(expectedConcurrencyVersion !== undefined ? { expectedConcurrencyVersion } : {}),
  };
}
